"use client";

import { type Ref, useEffect, useState } from "react";

/** True on macOS and iOS, where the search shortcut is Cmd+K. */
export function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /mac|iphone|ipad|ipod/i.test(navigator.platform);
}

/** Cmd+K on Apple platforms and Ctrl+K elsewhere, with no other modifiers. */
export function isSearchShortcut(event: KeyboardEvent): boolean {
  if (event.altKey || event.shiftKey || event.key.toLowerCase() !== "k")
    return false;
  return isApplePlatform()
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;
}

/**
 * Calls `onShortcut` for the search shortcut and cancels the browser's own
 * handling of it. Pass `enabled: false` where search is not available, so the
 * browser keeps its default. `onShortcut` should be stable (`useCallback`).
 */
export function useSearchShortcut(onShortcut: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSearchShortcut(event)) return;
      event.preventDefault();
      onShortcut();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, onShortcut]);
}

export interface SearchTriggerProps {
  onClick: () => void;
  /** Visible text and accessible name. */
  label?: string;
  /** Set while the dialog it opens is showing. */
  expanded?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * The persistent "Search" control in navigation. It shows the platform's
 * shortcut once mounted; the server render shows Ctrl K so hydration matches.
 */
export function SearchTrigger({
  onClick,
  label = "Search",
  expanded = false,
  ref,
}: SearchTriggerProps) {
  const [apple, setApple] = useState(false);
  useEffect(() => {
    setApple(isApplePlatform());
  }, []);
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-keyshortcuts={apple ? "Meta+K" : "Control+K"}
      className="flex w-full max-w-md cursor-pointer items-center gap-2 rounded-(--radius-field) border border-(--border-subtle) bg-(--bg-white) px-3 py-1.5 text-left text-(--fg-muted) hover:border-(--border-strong) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)"
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="shrink-0"
        aria-hidden={true}
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <span className="flex-1 text-[12.5px]">{label}</span>
      <kbd className="shrink-0 rounded border border-(--border-subtle) px-1.5 py-0.5 text-[10px]">
        {apple ? "⌘K" : "Ctrl K"}
      </kbd>
    </button>
  );
}
